import type { BatchCase, BatchPhase } from "../../hooks/useBatchUpload";

export interface BatchMonitorSummary {
  label: string;
  detail: string;
  percent: number;
}

export interface BatchProgressCounts {
  total: number;
  uploaded: number;
  processed: number;
  ready: number;
  errors: number;
}

export function batchProgressCounts(cases: BatchCase[]): BatchProgressCounts {
  const uploaded = cases.filter((item) =>
    ["uploaded", "analyzing", "ready", "failed"].includes(item.status),
  ).length;
  const processed = cases.filter(
    (item) => item.status === "ready" || item.status === "failed",
  ).length;
  const ready = cases.filter((item) => item.status === "ready").length;
  const errors = cases.filter(
    (item) => item.status === "failed" || item.status === "upload-failed",
  ).length;
  return { total: cases.length, uploaded, processed, ready, errors };
}

export function batchMonitorSummary(phase: BatchPhase, cases: BatchCase[]): BatchMonitorSummary {
  const { total, uploaded, processed, ready, errors } = batchProgressCounts(cases);

  if (phase === "parsing") {
    return { label: "Reading folder", detail: "Grouping files into studies…", percent: 0 };
  }
  if (phase === "review") {
    return {
      label: "Ready to review",
      detail: `${total} ${total === 1 ? "study" : "studies"} found`,
      percent: 0,
    };
  }
  if (phase === "uploading") {
    const percent = total
      ? Math.round(cases.reduce((sum, item) => sum + item.progress, 0) / total)
      : 0;
    return {
      label: "Uploading batch",
      detail: `${uploaded} of ${total} studies uploaded`,
      percent,
    };
  }
  if (phase === "analyzing") {
    return {
      label: "Analyzing batch",
      detail: `${processed} of ${total} studies processed`,
      percent: total ? Math.round(((ready + errors) / total) * 100) : 0,
    };
  }

  return {
    label: errors ? "Batch finished with issues" : "Batch complete",
    detail: errors ? `${ready} ready · ${errors} failed` : `${ready} studies ready`,
    percent: 100,
  };
}
